import { useCallback, useEffect, useRef, useState } from 'react';
import {
  deleteAttachment,
  saveAttachmentAs,
  uploadAttachment,
  validateFile,
} from '../../../../../services/attachmentService';
import {
  makeDocUploadNote,
  readDisplayedFiles,
  toFileRef,
} from '../../../utils/fileOverrides';
import { makeMediaThumbnail } from '../../../utils/imageThumbnail';
import { useStaleThumbRefresh } from './useStaleThumbRefresh';

const pickFirstFile = (list) => {
  if (!list?.length) return null;
  return list[0] || null;
};

export const useFileUploadSlot = ({
  block,
  fileOverrides,
  onFilesChange,
  missionId,
  docId,
}) => {
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [pending, setPending] = useState([]);
  const canUpload = Boolean(missionId && onFilesChange && docId);
  const files = readDisplayedFiles(block.id, fileOverrides);
  const filesRef = useRef(files);
  useEffect(() => {
    filesRef.current = files;
  }, [files]);

  const reportFiles = useCallback(
    (next) => {
      onFilesChange?.(block.id, next);
    },
    [block.id, onFilesChange]
  );

  useStaleThumbRefresh({
    canUpload,
    files,
    filesRef,
    missionId,
    reportFiles,
  });

  const runUpload = useCallback(
    async (file) => {
      if (!canUpload || !file) return;
      const check = validateFile(file);
      const localThumb = file.type?.startsWith('image/')
        ? URL.createObjectURL(file)
        : null;
      const job = {
        id: `pending-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        file,
        filename: file.name,
        progress: 6,
        error: check.valid ? null : check.error,
        localThumb,
      };
      setPending([job]);

      if (job.error) return;
      const thumbPromise = makeMediaThumbnail(job.file);
      if (
        file.type?.startsWith('video/') ||
        /\.(mp4|webm|mov|m4v|qt)$/i.test(file.name || '')
      ) {
        thumbPromise.then((dataUrl) => {
          if (!dataUrl) return;
          setPending((prev) =>
            prev.map((row) =>
              row.id === job.id ? { ...row, localThumb: dataUrl } : row
            )
          );
        });
      }
      try {
        const [uploaded, thumbDataUrl] = await Promise.all([
          uploadAttachment(
            missionId,
            job.file,
            (pct) => {
              setPending((prev) =>
                prev.map((row) =>
                  row.id === job.id ? { ...row, progress: pct } : row
                )
              );
            },
            makeDocUploadNote(docId, block.id)
          ),
          thumbPromise,
        ]);
        const ref = toFileRef({ ...uploaded, thumbDataUrl });
        if (ref) {
          const previous = filesRef.current;
          filesRef.current = [ref];
          reportFiles([ref]);
          previous.forEach((old) => {
            if (old.attachmentId && old.attachmentId !== ref.attachmentId) {
              deleteAttachment(missionId, old.attachmentId).catch(() => {});
            }
          });
        }
        setPending((prev) => prev.filter((row) => row.id !== job.id));
        if (localThumb) {
          requestAnimationFrame(() => URL.revokeObjectURL(localThumb));
        }
      } catch (err) {
        setPending((prev) =>
          prev.map((row) =>
            row.id === job.id
              ? { ...row, error: err.message || 'Upload failed' }
              : row
          )
        );
      }
    },
    [block.id, canUpload, docId, missionId, reportFiles]
  );

  const handlePick = (e) => {
    const chosen = pickFirstFile(e.target.files);
    e.target.value = '';
    runUpload(chosen);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    if (!canUpload) return;
    runUpload(pickFirstFile(e.dataTransfer.files));
  };

  const handleRemove = async (attachmentId) => {
    const next = filesRef.current.filter((f) => f.attachmentId !== attachmentId);
    filesRef.current = next;
    reportFiles(next);
    try {
      await deleteAttachment(missionId, attachmentId);
    } catch {
      /* unlink still stands — reset/undo can restore the ref */
    }
  };

  const handleDownload = (file) => {
    saveAttachmentAs(missionId, file.attachmentId, file.filename).catch(() => {});
  };

  const dismissPending = (id) => {
    setPending((prev) => {
      prev.forEach((row) => {
        if (row.id === id && row.localThumb) URL.revokeObjectURL(row.localThumb);
      });
      return prev.filter((row) => row.id !== id);
    });
  };

  const retryPending = (job) => {
    setPending((prev) => prev.filter((row) => row.id !== job.id));
    if (job.file) runUpload(job.file);
  };

  const dragHandlers = canUpload
    ? {
        onDragEnter: (e) => {
          e.preventDefault();
          e.stopPropagation();
          setDragOver(true);
        },
        onDragOver: (e) => {
          e.preventDefault();
          e.stopPropagation();
          setDragOver(true);
        },
        onDragLeave: (e) => {
          e.preventDefault();
          e.stopPropagation();
          setDragOver(false);
        },
        onDrop: handleDrop,
      }
    : {};

  return {
    canUpload,
    files,
    pending,
    dragOver,
    dragHandlers,
    fileInputRef,
    openPicker: () => fileInputRef.current?.click(),
    handlePick,
    handleRemove,
    handleDownload,
    dismissPending,
    retryPending,
  };
};
